import IconButton from "@mui/material/IconButton";
import { makeStyles } from "tss-react/mui";
import React from "react";
import { ReactZoomPanPinchRef, TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";
import  Tooltip from "@mui/material/Tooltip";
import { useMediaQuery } from "@mui/material";
import { Theme } from "@mui/material/styles";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import CloseFullScreenIcon from "@mui/icons-material/CloseFullscreen";
import CenterMapIcon from "@mui/icons-material/MyLocation";
import { useTranslation } from "react-i18next";

const MIN_SCALE = 0.5;

const useStyles = makeStyles()((theme) => ({
  controlButtons: {
    position: "absolute",
    right: theme.spacing(1),
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(1),
    zIndex: 1,
    "& button": {
      minWidth: theme.spacing(4),
      color: theme.palette.grey[700],
      backgroundColor: "white",
      boxShadow: theme.shadows[1],
      borderRadius: theme.spacing(1),
    },
    bottom: theme.spacing(1),
  },
}));

interface ZoomTransformWrapperProps {
  children: React.ReactNode;
  additionalControls?: React.ReactNode;
  initialScale?: number;
  initialPositionX?: number;
  initialPositionY?: number;
  disablePan?: boolean;
  disableZoom?: boolean;
  showRecenter?: boolean;
  wheelStep?: number;
  centerOnInit?: boolean;
  showFullScreen?: boolean;
  showEnableFullScreenAlways?: boolean;
  fullScreen?: boolean;
  disabled?: boolean;
  fullContent?: boolean;
  onZoomChange?: (scale: number) => void;
  onFullScreenChange?: () => void;
}

const ZoomTransformWrapper = React.forwardRef<ReactZoomPanPinchRef, ZoomTransformWrapperProps>(
  (
    {
      children,
      additionalControls = null,
      initialScale = 1,
      initialPositionX = 0,
      initialPositionY = 0,
      disablePan = false,
      disableZoom = false,
      showRecenter = false,
      centerOnInit = false,
      wheelStep = 0.1,
      showFullScreen = false,
      showEnableFullScreenAlways = false,
      fullContent = false,
      fullScreen,
      disabled = false,
      onZoomChange = null,
      onFullScreenChange,
    },
    ref
  ) => {
    const { t } = useTranslation();
    const { classes } = useStyles();
    const matchesLgUp = useMediaQuery((theme: Theme) => theme.breakpoints.up("lg"));

    const zoomChangeHandler = (ref: any) => {
      if (onZoomChange !== null) {
        onZoomChange(Math.max(ref.state.scale ?? initialScale, MIN_SCALE));
      }
    };

    const transformComponentWithButtons: any = ({ centerView, ...rest }: { centerView: (scale?: number) => void }) => (
      <React.Fragment>
        <div className={classes.controlButtons}>
          {additionalControls}
          {showRecenter && (
            <Tooltip title={t("info-tooltip-recenter-image")}>
              <IconButton onClick={() => centerView(initialScale)} data-testid="button-center-map-id">
                <CenterMapIcon />
              </IconButton>
            </Tooltip>
          )}
          {(showEnableFullScreenAlways || (showFullScreen && !matchesLgUp)) && (
            <Tooltip title={fullScreen ? t("info-tooltip-close-fullscreen") : t("info-tooltip-open-fullscreen")}>
              <IconButton onClick={onFullScreenChange} data-testid="button-fullscreen-id">
                {fullScreen ? <CloseFullScreenIcon /> : <FullscreenIcon />}
              </IconButton>
            </Tooltip>
          )}
        </div>

        <TransformComponent
          contentStyle={fullContent ? { width: "100%", height: "100%" } : undefined}
          wrapperStyle={{ width: "100%", height: "100%" }}
        >
          {children}
        </TransformComponent>
      </React.Fragment>
    );

    return (
      <div style={{ height: "100%", width: "100%" }}>
      <TransformWrapper
        limitToBounds={false}
        minScale={disableZoom ? 1 : MIN_SCALE}
        maxScale={disableZoom ? 1 : undefined}
        panning={{ disabled: disablePan, velocityDisabled: true }}
        disabled={disabled}
        centerOnInit={centerOnInit}
        pinch={{ step: wheelStep }}
        doubleClick={{ disabled: true }}
        wheel={{ step: wheelStep }}
        onZoom={zoomChangeHandler}
        onZoomStop={zoomChangeHandler}
        initialPositionX={initialPositionX}
        initialPositionY={initialPositionY}
        initialScale={initialScale}
        zoomAnimation={{ size: MIN_SCALE }}
        ref={ref}
      >
        {transformComponentWithButtons}
      </TransformWrapper>
      </div>
    );
  }
);

export default ZoomTransformWrapper;
